import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Moment from "react-moment";
import "moment/locale/pt-br";
import "./style.css";
import axios from "axios";
import { FeedPostMenu } from "../shared";
import EditPostPopup from "../../editPostPopup";

export default function PetitionFeed() {
  const { user } = useSelector((state) => ({ ...state }));
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedPetition, setSelectedPetition] = useState(null);
  const [editingPost, setEditingPost] = useState(null);

  const categories = [
    { id: "all", name: "Todas" },
    { id: "environment", name: "Meio Ambiente" },
    { id: "human_rights", name: "Direitos Humanos" },
    { id: "animal_rights", name: "Direitos Animais" },
    { id: "education", name: "Educação" },
    { id: "health", name: "Saúde" },
    { id: "politics", name: "Política" },
    { id: "economy", name: "Economia" },
    { id: "local", name: "Local" },
  ];

  const fetchPosts = async (pageNum = 1) => {
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/feed/petition?page=${pageNum}&limit=12`;
      if (category !== "all") url += `&category=${category}`;

      const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${user.token}` } });
      if (pageNum === 1) setPosts(data.posts || []);
      else setPosts((prev) => [...prev, ...(data.posts || [])]);
      setHasMore((data.pagination?.page || 1) < (data.pagination?.pages || 1));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching petition:", error);
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); fetchPosts(1); }, [category]);

  const handleEditPost = (post) => {
    setSelectedPetition(null);
    setEditingPost(post);
  };

  const handlePostUpdated = (updatedPost) => {
    setPosts((prev) => prev.map((p) => (p._id === updatedPost._id ? updatedPost : p)));
    setEditingPost(null);
  };

  const handlePostDeleted = (postId) => {
    setPosts((prev) => prev.filter((p) => p._id !== postId));
    setSelectedPetition(null);
  };

  const getProgressPercent = (signatures, goal) => {
    if (!goal || goal === 0) return 0;
    return Math.min(100, Math.round((signatures / goal) * 100));
  };

  if (loading && posts.length === 0) {
    return <div className="petition_feed_loading"><div className="petition_loader"></div><span>Carregando petições...</span></div>;
  }

  return (
    <div className="petition_feed">
      <div className="petition_header">
        <h1>Petições</h1>
        <p>Faça sua voz ser ouvida</p>
      </div>

      <div className="petition_filters">
        {categories.map((c) => (
          <button key={c.id} className={category === c.id ? "active" : ""} onClick={() => setCategory(c.id)}>
            {c.name}
          </button>
        ))}
      </div>

      {posts.length === 0 ? (
        <div className="petition_feed_empty">
          <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
            <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
          </svg>
          <h3>Nenhuma petição encontrada</h3>
          <p>Crie a primeira!</p>
        </div>
      ) : (
        <div className="petition_list">
          {posts.map((post) => {
            const percent = getProgressPercent(post.petitionData?.signatures, post.petitionData?.goal);
            return (
              <div key={post._id} className="petition_card" onClick={() => setSelectedPetition(post)}>
                {post.petitionData?.image && (
                  <div className="petition_card_image">
                    <img src={post.petitionData.image} alt="" />
                  </div>
                )}
                <div className="petition_card_content">
                  <span className="petition_category">
                    {categories.find(c => c.id === post.petitionData?.category)?.name || "Petição"}
                  </span>
                  <h3>{post.petitionData?.title || post.text}</h3>
                  <p className="petition_description">{post.petitionData?.description}</p>

                  <div className="petition_progress">
                    <div className="petition_progress_bar">
                      <div className="petition_progress_fill" style={{ width: `${percent}%` }}></div>
                    </div>
                    <div className="petition_progress_info">
                      <span className="petition_signatures">
                        <strong>{(post.petitionData?.signatures || 0).toLocaleString("pt-BR")}</strong> assinaram
                      </span>
                      <span className="petition_goal">
                        Meta: {(post.petitionData?.goal || 0).toLocaleString("pt-BR")}
                      </span>
                    </div>
                  </div>

                  <div className="petition_meta">
                    <Link to={`/profile/${post.user.username}`} className="petition_author" onClick={(e) => e.stopPropagation()}>
                      <img src={post.user.picture} alt="" />
                      <span>{post.user.first_name} {post.user.last_name}</span>
                    </Link>
                    <span className="petition_target">Para: {post.petitionData?.target || "Autoridades"}</span>
                  </div>

                  <button className="petition_sign_btn">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z"/>
                    </svg>
                    Assinar petição
                  </button>
                  <div onClick={(e) => e.stopPropagation()}>
                    <FeedPostMenu
                      post={post}
                      onEdit={handleEditPost}
                      onPostDeleted={handlePostDeleted}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hasMore && (
        <div className="petition_load_more">
          <button onClick={() => { setPage(p => p + 1); fetchPosts(page + 1); }} disabled={loading}>
            {loading ? "Carregando..." : "Ver mais"}
          </button>
        </div>
      )}

      {editingPost && (
        <EditPostPopup
          post={editingPost}
          user={user}
          setVisible={(visible) => !visible && setEditingPost(null)}
          onPostUpdated={handlePostUpdated}
        />
      )}

      {selectedPetition && (
        <div className="petition_modal_overlay" onClick={() => setSelectedPetition(null)}>
          <div className="petition_modal" onClick={(e) => e.stopPropagation()}>
            <button className="petition_modal_close" onClick={() => setSelectedPetition(null)}>✕</button>

            {selectedPetition.petitionData?.image && (
              <div className="petition_modal_header">
                <img src={selectedPetition.petitionData.image} alt="" />
              </div>
            )}

            <div className="petition_modal_body">
              <span className="petition_modal_category">
                {categories.find(c => c.id === selectedPetition.petitionData?.category)?.name}
              </span>
              <h2>{selectedPetition.petitionData?.title}</h2>

              <div className="petition_modal_target">
                <span className="petition_target_label">Esta petição é direcionada para:</span>
                <span className="petition_target_value">{selectedPetition.petitionData?.target}</span>
              </div>

              <div className="petition_modal_progress">
                <div className="petition_modal_signatures">
                  <span className="petition_modal_count">
                    {(selectedPetition.petitionData?.signatures || 0).toLocaleString("pt-BR")}
                  </span>
                  <span className="petition_modal_label">assinaturas</span>
                </div>
                <div className="petition_modal_progress_bar">
                  <div
                    className="petition_modal_progress_fill"
                    style={{ width: `${getProgressPercent(selectedPetition.petitionData?.signatures, selectedPetition.petitionData?.goal)}%` }}
                  ></div>
                </div>
                <div className="petition_modal_goal">
                  Meta: {(selectedPetition.petitionData?.goal || 0).toLocaleString("pt-BR")} assinaturas
                </div>
              </div>

              <div className="petition_modal_story">
                <h4>Sobre a petição</h4>
                <p>{selectedPetition.petitionData?.story || selectedPetition.text}</p>
              </div>

              {selectedPetition.petitionData?.updates?.length > 0 && (
                <div className="petition_modal_updates">
                  <h4>Atualizações</h4>
                  {selectedPetition.petitionData.updates.map((update, i) => (
                    <div key={i} className="petition_update_item">
                      <span className="petition_update_date">
                        <Moment format="DD/MM/YYYY">{update.date}</Moment>
                      </span>
                      <p>{update.content}</p>
                    </div>
                  ))}
                </div>
              )}

              <Link to={`/profile/${selectedPetition.user.username}`} className="petition_creator">
                <img src={selectedPetition.user.picture} alt="" />
                <div>
                  <span className="petition_creator_label">Criada por</span>
                  <span className="petition_creator_name">{selectedPetition.user.first_name} {selectedPetition.user.last_name}</span>
                </div>
              </Link>

              <div className="petition_modal_actions">
                <button className="petition_modal_sign_btn">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z"/>
                  </svg>
                  Assinar esta petição
                </button>
                <button className="petition_share_btn">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/>
                  </svg>
                  Compartilhar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
